/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import config from '@linc-technologies/linced-common/utils/config';
import {
  isMobile,
  isTWA,
} from '@linc-technologies/linced-common/utils/platform';
import { and } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';

export default class PostAttachment extends Component {
  @service
  fetch;

  @service
  toaster;

  @service
  uppy;

  MediaSource = MediaSource;
  MediaType = MediaType;

  // replacementEnabled determines whether media replacement is enabled
  // (if applicable to this media record).
  replacementEnabled = false;

  // canReplace determines whether to allow media replacement.
  @and('replacementEnabled', 'record.canReplace')
  canReplace;

  // uploading flags that a replacement file is currently uploading.
  @tracked
  uploading = false;

  // allowedFileTypes optionally provides the types accepted when replacing a
  // non-image file. Every caller passes the same list its attachment picker
  // uses, which is why this stays a plain property.
  allowedFileTypes = null;

  // replacementFileTypes resolves what a replacement upload actually accepts,
  // mirroring the image/file split the template passes as `@mode`. Images come
  // from the uppy service so a replacement accepts exactly what the attachment
  // picker accepted, GIF included; anything else honours the caller's list.
  //
  // Deliberately a separate name from `allowedFileTypes`: callers pass that in,
  // and an incoming attr would clobber a computed of the same name, handing the
  // document list to image replacements.
  @computed(
    'allowedFileTypes',
    'record.mediaType',
    'uppy.{allowedAttachmentImageTypes,allowedFileTypes}',
  )
  get replacementFileTypes() {
    if (this.get('record.mediaType') === MediaType.IMAGE) {
      return this.uppy.allowedAttachmentImageTypes;
    }

    return this.allowedFileTypes ?? this.uppy.allowedFileTypes;
  }

  // `recordOverride` lets callers override the computed default; Ember < 4
  // allowed that implicitly (computed-property.override).
  recordOverride = null;

  @computed('attachment', 'attachment.record', 'recordOverride')
  get record() {
    if (this.recordOverride !== null) {
      return this.recordOverride;
    }

    let record = this.get('attachment.record');
    if (record) {
      return record;
    }
    return this.attachment;
  }

  @computed('record.mimeType')
  get colour() {
    const defaultChoice = '#cbcbcb';
    if (!this.record.mimeType) {
      return defaultChoice;
    }

    let fileTypeGeneral = this.record.mimeType.split('/')[0];

    switch (fileTypeGeneral) {
      case 'text':
        return '#cbcbcb';
      case 'audio':
        return '#1abc9c';
      case 'video':
        return '#2980b9';
      case 'image':
        return '#f2f2f2';
      case 'application':
        if (this.record.mimeType === 'application/pdf') {
          return '#e74c3c';
        }
        break;
    }

    return defaultChoice;
  }

  @computed('record.mimeType')
  get bgStyle() {
    return this.colour ? htmlSafe(`background-color: ${this.colour};`) : '';
  }

  // failedPreviewUrls collects the preview URLs that failed to load, so each
  // candidate is tried once and then skipped. Google Drive thumbnail links are
  // captured on the record at pick time and only resolve while the file's
  // sharing permission holds, and signed URLs expire, so a stored thumbnail can
  // 403 long after it was written.
  //
  // Keyed on the URL rather than a single "it failed" flag: a re-signed link, a
  // reloaded record, or a different attachment on a reused component all yield
  // a URL that isn't in this list, and so get a fresh attempt. A flag would
  // latch on and leave the icon showing for a URL that now works.
  //
  // A native class field, so each component gets its own array and one
  // attachment's failures can't retire another's URLs.
  @tracked
  failedPreviewUrls = [];

  // previewUrl provides the best available preview image that hasn't already
  // failed, or null to fall back to the file type icon.
  @computed(
    'attachment.file.preview',
    'failedPreviewUrls',
    'record.{thumbnailSignedUrl,thumbnailUrl}',
  )
  get previewUrl() {
    let failed = this.failedPreviewUrls ?? [];

    return (
      [
        this.attachment?.file?.preview,
        this.record?.thumbnailSignedUrl,
        this.record?.thumbnailUrl,
      ].find((url) => url && !failed.includes(url)) ?? null
    );
  }

  @computed('record.fileName')
  get extension() {
    let fileName = this.get('record.fileName');
    return fileName ? fileName.replace(/.*\.([^.]+)$/, '$1') : '';
  }

  // isMobile flags the native mobile apps, including the Android TWA, where
  // links open outside the page that owns the app's blobs (HERO-11042). In
  // the TWA a blob link is handed back to Android, which relaunches the app
  // instead of opening the file. Resolved on init unless passed in.
  isMobile = null;

  // localUrl is an object URL for the locally selected file, so pending
  // uploads can be opened before the media record has been persisted.
  // Unavailable on mobile, so unsaved attachments there aren't linked.
  @tracked
  localUrl = null;

  // href links to the persisted media, falling back to the local file while
  // the record is unsaved (rawUrl needs an ID, otherwise the API 422s).
  @computed('record.{id,isNew,rawUrl}', 'localUrl')
  get href() {
    if (this.get('record.isNew') || !this.get('record.id')) {
      return this.localUrl;
    }
    return this.get('record.rawUrl');
  }

  init() {
    super.init(...arguments);
    if (this.isMobile === null) {
      this.isMobile = isMobile() || isTWA();
    }
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let data = this.attachment?.file?.data;
    if (data === this._localData) {
      return;
    }
    this._revokeLocalUrl();
    this._localData = data;
    this.localUrl =
      !this.isMobile && data instanceof Blob ? URL.createObjectURL(data) : null;
  }

  willDestroy() {
    this._revokeLocalUrl();
    super.willDestroy(...arguments);
  }

  _revokeLocalUrl() {
    if (this.localUrl) {
      URL.revokeObjectURL(this.localUrl);
    }
  }

  @action
  removeAttachment(attachment) {
    this.remove(attachment);
  }

  // handlePreviewError retires the URL that just failed, so previewUrl moves
  // to the next candidate and finally to the file type icon. Each failure
  // removes one candidate, so this terminates rather than cycling.
  // Replaces the array rather than pushing into it, so the invalidation still
  // works once this is a @tracked field on a Glimmer component.
  @action
  handlePreviewError(url) {
    let failed = this.failedPreviewUrls ?? [];
    if (!url || failed.includes(url)) {
      return;
    }
    this.failedPreviewUrls = [...failed, url];
  }

  @action
  startUpload() {
    if (!this.canReplace) {
      return;
    }
    this.uploading = true;
  }

  @action
  handleUpload(file, upload) {
    if (!this.canReplace) {
      return;
    }

    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);

    let mediaType =
      MediaType.getValue(file.type.split('/')[0]) || MediaType.FILE;

    let replacement = {
      mediaType,
      mediaSource: mediaSource,
      mimeType: file.type,
      fileName: file.name,
      storageKey: key,
      fileSize: file.size,
    };

    let data = {
      mediaId: this.get('record.id'),
      oldStorageKey: this.get('record.storageKey'),
      replacement,
    };

    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(
        `${config.APP.apiHost}/media/${config.APP.apiPath}/media/:replace`,
        options,
      )
      .then(() => {
        this.record.reload();
        // The file behind the URLs has changed, so retry every candidate,
        // including any that a stable thumbnail URL had already retired.
        this.failedPreviewUrls = [];
        this.toaster.success('File uploading successfully.');
      })
      .catch(() => {
        this.toaster.error('Error uploading file.');
      })
      .finally(() => {
        this.uploading = false;
      });
  }

  @action
  handleUploadError(file, error) {
    // Clear `uploading` too, otherwise the spinner covers the attachment
    // indefinitely and the user can't retry.
    this.uploading = false;
    this.toaster.error(
      error?.message
        ? `Error uploading file: ${error.message}`
        : 'Error uploading file. Refresh the page and try again. If the error persists, please contact Customer Support.',
    );
  }
}
